import React from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
    TextField,
    TextFieldProps,
} from '@material-ui/core';
import {
    Autocomplete as MuiAutocomplete,
} from '@material-ui/lab';
import { AutocompleteChangeReason } from '@material-ui/lab/useAutocomplete/useAutocomplete';

export type AutocompleteOption = {
    text: string,
    value: string,
};

type AutocompleteProps = {
    label: string,
    options: Array<AutocompleteOption>,
    freeSolo?: boolean,
    onChange: (value: string, reason?: AutocompleteChangeReason | 'change') => void,
    value?: string,
    getOptionLabel?: (option: AutocompleteOption) => string,
    startAdornment?: React.ReactNode,
};

Autocomplete.defaultProps = {
    freeSolo: false,
    value: undefined,
    getOptionLabel: (option: AutocompleteOption) => {
        if (typeof option === 'string') {
            return option;
        }

        return `${option.text}`;
    },
    startAdornment: null,
};

const useStyle = makeStyles((theme: Theme) => createStyles({
    autocomplete: {
        display: 'flex',
    },
    textField: {
        flex: 1,
    },
}));

function Autocomplete(props: AutocompleteProps): React.ReactElement {
    const {
        label,
        options,
        freeSolo,
        onChange,
        value,
        getOptionLabel = Autocomplete.defaultProps.getOptionLabel,
        startAdornment,
    } = props;
    const classes = useStyle();

    return (
        <MuiAutocomplete
            className={classes.autocomplete}
            options={options.map((option) => option)}
            getOptionLabel={getOptionLabel}
            freeSolo={freeSolo}
            renderInput={(params: TextFieldProps) => (
                <TextField
                    {...params}
                    className={classes.textField}
                    label={label}
                    variant="outlined"
                    onChange={(e) => onChange(e.target.value, 'change')}
                    InputProps={{
                        ...params.InputProps,
                        startAdornment,
                    }}
                />
            )}
            onChange={(e, v, reason) => {
                if (typeof v === 'string') {
                    onChange(v, reason);
                } else if (v === null) {
                    onChange('', reason);
                } else if (reason === 'select-option') {
                    onChange(v.value, reason);
                }
            }}
            value={value}
            inputValue={value}
        />
    );
}

export { Autocomplete };
